import {
  Network, NETWORK_IDS, NETWORKS, POSTING_ACCOUNT_TYPE,
} from 'lib/constants';
import { NetworkIcon } from 'elements/NetworkIcon';
import { trackClickSocialMediaLinkEvent } from 'lib/hooks/useEventTracking';
import { User } from 'lib/types/users';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { MailButton } from 'components/MailButton';
import { postingAccountToUrl, userIsAdmin } from 'lib/helpers';
import { AddPostingAccountsModal } from 'components/AddPostingAccountsModal';
import { useModal } from 'mui-modal-provider';
import { Add } from '@mui/icons-material';
import { MobileTooltip } from 'components/MobileTooltip';
import { useState, useEffect } from 'react';

interface Props {
  profileUser: User;
  showColor?: boolean;
  /** Should the icons always be centered (as on profile page) */
  alwaysCenter?: boolean;
}

export const SocialMediaRow = ({ profileUser, showColor, alwaysCenter = false }: Props) => {
  const API = useApi();
  const currentUser = useCurrentUser();
  const { showModal } = useModal();

  const [_postingAccounts, setPostingAccounts] = useState(profileUser.postingAccounts || []);

  useEffect(() => {
    setPostingAccounts(profileUser.postingAccounts || []);
  }, [profileUser.id]);

  const currentUserCanAddPostingAccount = userIsAdmin(currentUser)
    || currentUser?.hasPaidTeam;

  const accountsToRender = _postingAccounts
    .filter((pa) => pa.type !== POSTING_ACCOUNT_TYPE.manager)
    .filter((pa) => pa.showOnProfile)
    .filter((pa) => !!postingAccountToUrl(pa.network, pa.account));

  const hasMailButton = !!profileUser?.publicSettings?.contactAllowed;
  const hasPersonalUrl = !!profileUser.personalUrl;

  const addPostingAccount = async () => {
    const modal = showModal(AddPostingAccountsModal, {
      user: profileUser,
      postingAccounts: _postingAccounts,
      onAdd: async (accountsToAdd) => {
        const newPostingAccounts = await API.addPostingAccounts(accountsToAdd, profileUser.id);

        setPostingAccounts([..._postingAccounts, ...newPostingAccounts]);
        modal.destroy();
      },
      onClose: () => {
        modal.destroy();
      },
    });
  };

  const hasSocialMediaOrContact = () => {
    const {
      personalUrl, postingAccounts,
    } = profileUser;
    const ownerAccounts = postingAccounts?.filter(
      (pa) => pa.type === POSTING_ACCOUNT_TYPE.owner || pa.showOnProfile,
    );
    return !!(profileUser && (profileUser?.publicSettings?.contactAllowed
      || personalUrl || ownerAccounts?.length));
  };

  if (!hasSocialMediaOrContact() && !currentUserCanAddPostingAccount) {
    return null;
  }

  const networkButton = (network: Network, networkUrl: string, followers?: number) => {
    if (!networkUrl.length) {
      return null;
    }
    return (
      <MobileTooltip title={followers ? `${followers.toLocaleString()} followers` : ''} key={`tooltip-${network.id}-${networkUrl}`}>
        <a
          key={`network-${network.id}-${networkUrl}`}
          href={networkUrl}
          title={`open ${network.title}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackClickSocialMediaLinkEvent(
            API, network.name, profileUser.id,
          )}
        >
          <NetworkIcon
            network={network.id}
            iconColor={showColor ? 'color' : undefined}
          />
        </a>
      </MobileTooltip>
    );
  };

  // If there are no accounts to render, and no personal url or mail button,
  // and the user can't add posting accounts, don't render anything.
  if (!accountsToRender.length
    && !hasPersonalUrl
    && !hasMailButton
    && !currentUserCanAddPostingAccount
  ) {
    return null;
  }

  return (
    <div
      id="profile-social-media"
      className={`flex flex-wrap w-full ${alwaysCenter ? 'justify-center' : 'justify-start'} gap-6 items-center md:gap-3`}
    >
      {!!profileUser?.publicSettings?.contactAllowed
        && (<MailButton user={profileUser} />)}
      {accountsToRender.map((pa) => {
        const networkUrl = postingAccountToUrl(pa.network, pa.account);
        if (!networkUrl.length) {
          return null;
        }
        const network = NETWORKS[pa.network];
        return networkButton(network, networkUrl, pa.networkAccount?.followersCount);
      })}
      {!!profileUser.personalUrl && networkButton(
        NETWORKS[NETWORK_IDS.personal], profileUser.personalUrl,
      )}
      {currentUserCanAddPostingAccount && (
        <MobileTooltip title="Add Posting Account">
          <span className="cursor-pointer">
            <Add color="primary" onClick={addPostingAccount} />
          </span>
        </MobileTooltip>
      )}
    </div>
  );
};
